/**
 * Pure helpers for the skill editor's file sidebar: extension classification,
 * new-file / rename path validation, and the flat-to-tree projection the rail
 * renders.
 *
 * It stays in the browser-shared layer so the rules can be unit-tested
 * headlessly. The Gateway re-validates every path on write — these rules give
 * the author an inline answer before a round-trip, they are not the security
 * boundary.
 */

export const PRIMARY_SKILL_FILE = "SKILL.md";

/** Extensions whose content the WYSIWYG body editor owns. */
const MARKDOWN_EXTENSIONS = new Set(["md", "markdown"]);

/**
 * Extensions read and written as UTF-8 text. Mirrors the Gateway's
 * `CONTENT_TYPE_BY_EXT` text set (`skill-content-types.ts`): anything not in
 * this set is treated as binary and carried base64, so an uploaded `.zip` is
 * never decoded as text and shown as mojibake.
 */
const TEXT_EXTENSIONS = new Set([
  "md",
  "markdown",
  "txt",
  "log",
  "csv",
  "json",
  "js",
  "mjs",
  "cjs",
  "ts",
  "py",
  "sh",
]);

/** Basenames the Gateway reserves at any depth in a skill directory. */
const RESERVED_BASENAMES = new Set([PRIMARY_SKILL_FILE, "config.schema.json"]);

/**
 * Mirror of `normalizeRelativePath`'s per-segment and whole-path ceilings
 * (`packages/gateway/src/util/paths.ts`), so the dialog rejects an over-long
 * path with the same answer the Gateway would give.
 */
const MAX_SEGMENT_LENGTH = 128;
const MAX_PATH_LENGTH = 512;

/** Lowercased extension without the leading dot, or `""` when there is none. */
export function fileExtension(path: string): string {
  const name = path.split("/").at(-1) ?? path;
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

export function isMarkdownFile(path: string): boolean {
  return MARKDOWN_EXTENSIONS.has(fileExtension(path));
}

/** Whether this path's bytes are carried as UTF-8 text (else base64 binary). */
export function isTextFile(path: string): boolean {
  return TEXT_EXTENSIONS.has(fileExtension(path));
}

/** The wire encoding a companion at `path` is stored under. Unknown ⇒ binary. */
export function encodingForPath(path: string): "utf8" | "base64" {
  return isTextFile(path) ? "utf8" : "base64";
}

/**
 * Re-express a companion's stored content under a new encoding, preserving the
 * underlying bytes. Encoding is decided by extension, so a rename that crosses
 * the text/binary boundary (`logo.png` → `logo.txt`) must convert the payload
 * rather than relabel it — leaving a base64 string tagged `utf8` would store the
 * base64 text as the file's bytes.
 */
export function reencodeCompanionContent(
  content: string,
  from: "utf8" | "base64",
  to: "utf8" | "base64",
): string {
  if (from === to) return content;
  const bytes = from === "base64" ? base64ToBytes(content) : new TextEncoder().encode(content);
  return to === "base64" ? bytesToBase64(bytes) : new TextDecoder().decode(bytes);
}

/** Base64-encode raw bytes in chunks so a large buffer never overflows the stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Decode a base64 string back to raw bytes (for downloading a binary companion). */
export function base64ToBytes(content: string): Uint8Array {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Decoded byte length of a companion's stored content. */
export function decodedByteLength(content: string, encoding: "utf8" | "base64"): number {
  if (encoding === "base64") {
    // atob throws on malformed input; a partially-typed row can be malformed,
    // so fall back to a length estimate rather than crashing the editor.
    try {
      return atob(content).length;
    } catch {
      return Math.floor((content.length * 3) / 4);
    }
  }
  return new TextEncoder().encode(content).byteLength;
}

export interface NewFileValidation {
  ok: boolean;
  error?: string;
}

/**
 * Validate a path the author typed into the new-file or rename dialog. Folders
 * are allowed — nested paths split on `/` and each segment is checked — so this
 * mirrors the Gateway's `validateCompanionPath` / `normalizeRelativePath`
 * rules. `existingPaths` excludes the path being renamed so renaming a file to
 * its own current name is a no-op, not a duplicate.
 */
export function validateNewFilePath(
  name: string,
  existingPaths: readonly string[],
): NewFileValidation {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "Enter a file name (e.g. references/rubric.md)." };
  }
  if (trimmed.length > MAX_PATH_LENGTH) {
    return { ok: false, error: `Path must be ${MAX_PATH_LENGTH} characters or fewer.` };
  }
  if (trimmed.startsWith("/")) {
    return { ok: false, error: "Path cannot start with a slash." };
  }
  const segments = trimmed.split("/");
  for (const segment of segments) {
    if (segment.length === 0) {
      return { ok: false, error: "Path cannot contain an empty folder name." };
    }
    if (segment === "." || segment === "..") {
      return { ok: false, error: `"${segment}" is not a valid folder name.` };
    }
    if (segment.length > MAX_SEGMENT_LENGTH) {
      return { ok: false, error: `Each name must be ${MAX_SEGMENT_LENGTH} characters or fewer.` };
    }
  }
  const basename = segments.at(-1) ?? trimmed;
  if (RESERVED_BASENAMES.has(basename)) {
    return { ok: false, error: `${basename} is reserved.` };
  }
  if (existingPaths.includes(trimmed)) {
    return { ok: false, error: "A file with that name already exists." };
  }
  return { ok: true };
}

export interface SkillFileRow {
  path: string;
  dirty: boolean;
  /** Upload lifecycle for a companion whose disk write is in flight. */
  status?: "uploading" | "error";
  /** Failure reason surfaced on the row when `status === "error"`. */
  error?: string;
}

export interface SkillTreeFileNode {
  type: "file";
  /** Trailing path segment shown in the row. */
  name: string;
  /** Full POSIX path, the row's identity. */
  path: string;
  row: SkillFileRow;
}

export interface SkillTreeFolderNode {
  type: "folder";
  name: string;
  /** Full POSIX path of the folder (its accumulated prefix). */
  path: string;
  children: SkillTreeNode[];
}

export type SkillTreeNode = SkillTreeFileNode | SkillTreeFolderNode;

/**
 * Project the flat companion rows into a folder tree by splitting each path on
 * `/`. Folders are synthetic — they exist only because a file's path implies
 * them — so no folder record crosses the wire. Folders sort above files, each
 * group alphabetical; `SKILL.md` is pinned first at the root because it is the
 * skill's entry document. `pendingFolders` seeds empty folders that hold no
 * file yet (the "New folder" affordance); they render but never persist.
 */
export function buildSkillTree(
  rows: readonly SkillFileRow[],
  pendingFolders: readonly string[] = [],
): SkillTreeNode[] {
  const root: SkillTreeFolderNode = { type: "folder", name: "", path: "", children: [] };
  const folderByPath = new Map<string, SkillTreeFolderNode>([["", root]]);

  const ensureFolder = (segments: string[]): SkillTreeFolderNode => {
    let parent = root;
    let prefix = "";
    for (const segment of segments) {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      let folder = folderByPath.get(prefix);
      if (!folder) {
        folder = { type: "folder", name: segment, path: prefix, children: [] };
        folderByPath.set(prefix, folder);
        parent.children.push(folder);
      }
      parent = folder;
    }
    return parent;
  };

  for (const pending of pendingFolders) {
    const segments = pending.split("/").filter((segment) => segment.length > 0);
    if (segments.length > 0) ensureFolder(segments);
  }

  for (const row of rows) {
    const segments = row.path.split("/").filter((segment) => segment.length > 0);
    if (segments.length === 0) continue;
    const name = segments.at(-1) ?? row.path;
    const parent = ensureFolder(segments.slice(0, -1));
    parent.children.push({ type: "file", name, path: row.path, row });
  }

  sortSkillTree(root.children, true);
  return root.children;
}

function sortSkillTree(nodes: SkillTreeNode[], isRoot: boolean): void {
  nodes.sort((a, b) => {
    if (isRoot) {
      const aSkill = a.type === "file" && a.name === PRIMARY_SKILL_FILE;
      const bSkill = b.type === "file" && b.name === PRIMARY_SKILL_FILE;
      if (aSkill !== bSkill) return aSkill ? -1 : 1;
    }
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  for (const node of nodes) {
    if (node.type === "folder") sortSkillTree(node.children, false);
  }
}

/** Every folder-path ancestor of a file path, root-first (`a`, `a/b`, …). */
export function ancestorFolderPaths(path: string): string[] {
  const segments = path.split("/").filter((segment) => segment.length > 0);
  const ancestors: string[] = [];
  let prefix = "";
  for (let i = 0; i < segments.length - 1; i++) {
    prefix = prefix ? `${prefix}/${segments[i]}` : segments[i];
    ancestors.push(prefix);
  }
  return ancestors;
}
